import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { minLength } from '@adornis/validation/functions/length.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { MatchingWeekCategory } from './category.js';
import { Company } from './company.js';
import { overlapsInterval } from '../shared/time-slot-overlap.js';
import { isValidHttpUrl, normalizeHttpUrl } from '../shared/url.js';
import { JobApplication, JobApplicationStatus } from './job-application.js';
import { MatchingScoreCache } from './MatchingScoreCache.js';

export enum EmploymentType {
  INTERNSHIP = 'INTERNSHIP',
  WORKINGSTUDENT = 'WORKINGSTUDENT',
  THESIS = 'THESIS',
  TRAINEE = 'TRAINEE',
  FULLTIME = 'FULLTIME',
}

export enum SpeechLevel {
  A1 = 'A1',
  A2 = 'A2',
  B1 = 'B1',
  B2 = 'B2',
  C1 = 'C1',
  C2 = 'C2',
  NONE = 'NONE',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Speech levels offered in human-facing dropdowns (candidate self-assessment,
 * job-requirement editor, search filters). `UNKNOWN` is an internal fallback
 * (e.g. set by CV auto-extraction when the level can't be determined) and must
 * never be selectable — nobody would deliberately pick "Unbekannt".
 */
export const SELECTABLE_SPEECH_LEVELS: Record<string, string> = Object.fromEntries(
  Object.entries(SpeechLevel).filter(([key]) => !['_class', '_isBaseQLEnum', SpeechLevel.UNKNOWN].includes(key)),
);

export enum ExpectedAcademicTitle {
  BACHELOR = 'BACHELOR',
  MASTER = 'MASTER',
  DOCTORAL = 'DOCTORAL',
  STAATSEXAM = 'STAATSEXAM',
  OTHER = 'OTHER',
}

export enum PreferredStudyField {
  AGRICULTURE_ENVIRONMENT_FORESTRY = 'AGRICULTURE_ENVIRONMENT_FORESTRY',
  ARCHITECTURE = 'ARCHITECTURE',
  ARTS_MUSIC_DESIGN_FASHION = 'ARTS_MUSIC_DESIGN_FASHION',
  CIVIL_ENVIRONMENTAL_ENGINEERING = 'CIVIL_ENVIRONMENTAL_ENGINEERING',
  COMPUTER_SCIENCE = 'COMPUTER_SCIENCE',
  BUSINESS_MANAGEMENT = 'BUSINESS_MANAGEMENT',
  ELECTRICAL_ENGINEERING = 'ELECTRICAL_ENGINEERING',
  ENGINEERING_AND_TECHNOLOGY = 'ENGINEERING_AND_TECHNOLOGY',
  EVENT_TOURISM_HOSPITALITY = 'EVENT_TOURISM_HOSPITALITY',
  HEALTH_AND_MEDICINE = 'HEALTH_AND_MEDICINE',
  LAW_TAX_PUBLIC_ADMIN = 'LAW_TAX_PUBLIC_ADMIN',
  LANGUAGE_AND_CULTURAL_STUDIES = 'LANGUAGE_AND_CULTURAL_STUDIES',
  MECHANICAL_ENGINEERING = 'MECHANICAL_ENGINEERING',
  MEDIA_COMMUNICATION_MARKETING = 'MEDIA_COMMUNICATION_MARKETING',
  NATURAL_SCIENCES_AND_MATHEMATICS = 'NATURAL_SCIENCES_AND_MATHEMATICS',
  PSYCHOLOGY = 'PSYCHOLOGY',
  SOCIAL_SCIENCES = 'SOCIAL_SCIENCES',
  SPORTS_AND_FITNESS = 'SPORTS_AND_FITNESS',
  TEACHING_AND_EDUCATIONAL_SCIENCES = 'TEACHING_AND_EDUCATIONAL_SCIENCES',
  OTHER = 'OTHER',
}

export enum SoftSkills {
  TEAMWORK = 'TEAMWORK',
  LEADERSHIP = 'LEADERSHIP',
  COMMUNICATION = 'COMMUNICATION',
  PROBLEM_SOLVING = 'PROBLEM_SOLVING',
  ADAPTABILITY_FLEXIBILITY = 'ADAPTABILITY_FLEXIBILITY',
  ANALYTICAL_THINKING = 'ANALYTICAL_THINKING',
  CONFLICT_RESOLUTION = 'CONFLICT_RESOLUTION',
  CREATIVITY = 'CREATIVITY',
  CRITICAL_THINKING = 'CRITICAL_THINKING',
  DIGITAL_LITERACY_TECH_AFFINITY = 'DIGITAL_LITERACY_TECH_AFFINITY',
  EMPATHY_SOCIAL_SENSITIVITY = 'EMPATHY_SOCIAL_SENSITIVITY',
  ENTREPRENEURIAL_THINKING = 'ENTREPRENEURIAL_THINKING',
  INITIATIVE_PROACTIVITY = 'INITIATIVE_PROACTIVITY',
  INTERCULTURAL_COMPETENCE = 'INTERCULTURAL_COMPETENCE',
  PROJECT_MANAGEMENT = 'PROJECT_MANAGEMENT',
  RESPONSIBILITY_RELIABILITY = 'RESPONSIBILITY_RELIABILITY',
  RESILIENCE_STRESS_TOLERANCE = 'RESILIENCE_STRESS_TOLERANCE',
  STRUCTURED_INDEPENDENT_WORK = 'STRUCTURED_INDEPENDENT_WORK',
  TIME_MANAGEMENT = 'TIME_MANAGEMENT',
  WILLINGNESS_TO_LEARN_CURIOSITY = 'WILLINGNESS_TO_LEARN_CURIOSITY',
  NONE = 'NONE',
}

registerEnum({ enum: EmploymentType, name: 'EmploymentType' });
registerEnum({ enum: SpeechLevel, name: 'SpeechLevel' });
registerEnum({ enum: ExpectedAcademicTitle, name: 'ExpectedAcademicTitle' });
registerEnum({ enum: PreferredStudyField, name: 'PreferredStudyField' });
registerEnum({ enum: SoftSkills, name: 'SoftSkills' });

@Entity()
export class JobPosition extends MongoEntity {
  static override _class = 'JobPosition';
  static override _collectionName = 'jobPositions';

  @validate(nonOptional())
  @Field(() => ID)
  companyID: Maybe<string>;

  @DataLoadedField(() => Company, root => root.companyID)
  company: Maybe<Company>;

  @validate(nonOptional())
  @Field(() => String)
  title: Maybe<string>; // e.g., Junior Software Engineer

  @validate(nonOptional())
  @Field(() => EmploymentType)
  employmentType: Maybe<EmploymentType>; // e.g., Praktikum, Trainee-Programm, Direkteinstieg

  // `minLength(1)` (not `nonOptional`) because an empty array is truthy and would slip past `nonOptional`.
  @validate(minLength(1, 'validation_non_optional'))
  @Field(() => [TextMultipleLangItem])
  description: Maybe<TextMultipleLangItem[]>; // e.g., Rotation in Software Engineering & Data Science Teams

  @validate(nonOptional())
  @Field(() => String)
  location: Maybe<string>; // e.g., Frankfurt

  @validate(nonOptional())
  @Field(() => String)
  duration: Maybe<string>; // e.g., 6 Monate, 12 Monate

  @validate(nonOptional())
  @Field(() => Int, {
    resolve: () =>
      function (this: JobPosition) {
        return this.timeSlots?.length ?? 0;
      },
  })
  availableSlots: Maybe<number>; // e.g., 3, 5, 4

  @validate(nonOptional())
  @Field(() => Date)
  startDate: Maybe<Date>; // e.g., flexibel ab Juni 2026, ab sofort verfügbar

  @validate(nonOptional())
  @Field(() => ID)
  categoryID: Maybe<string>;

  @DataLoadedField(() => MatchingWeekCategory, root => root.categoryID)
  category: Maybe<MatchingWeekCategory>;

  // Applicant Requirements
  @validate(minLength(1, 'validation_non_optional'))
  @Field(() => [ExpectedAcademicTitle])
  expectedAcademicTitle: Maybe<ExpectedAcademicTitle[]>;

  @validate(minLength(1, 'validation_non_optional'))
  @Field(() => [PreferredStudyField])
  preferredStudyField: Maybe<PreferredStudyField[]>;

  @validate(nonOptional())
  @Field(() => SpeechLevel)
  languageRequirementsDE: Maybe<SpeechLevel>;

  @validate(nonOptional())
  @Field(() => SpeechLevel)
  languageRequirementsEN: Maybe<SpeechLevel>;

  @Field(() => String)
  languageRequirementsOther: Maybe<string>;

  @validate(nonOptional())
  @Field(() => String)
  mandatoryTechSkills: Maybe<string>;

  @Field(() => String)
  desiredTechSkills: Maybe<string>;

  @Field(() => String)
  optionalTechSkills: Maybe<string>;

  @validate(minLength(1, 'validation_soft_skills_too_few'))
  @Field(() => [SoftSkills])
  softSkills: Maybe<SoftSkills[]>;

  @validate(minLength(1, 'validation_non_optional'))
  @Field(() => [TextMultipleLangItem])
  practicalExperience: Maybe<TextMultipleLangItem[]>;

  @Field(() => [TimeSlot])
  timeSlots: Maybe<TimeSlot[]>;

  @validate(({ value, key }) => {
    if (value && !isValidHttpUrl(value))
      throw new ValidationError('Please enter a valid URL starting with https://.', {
        key,
        translationKey: 'validation_url_invalid',
      });
  })
  @Field(() => String)
  meetingLink: Maybe<string>;

  @Field(() => [TextMultipleLangItem])
  additionalRequirements: Maybe<TextMultipleLangItem[]>;

  @Field(() => Int, {
    resolve: () =>
      function (this: JobPosition) {
        return mongo().collection(JobApplication._collectionName).countDocuments({ jobPositionId: this._id });
      },
  })
  numApplicants: Maybe<number>;

  static override async beforeCreate(entity: JobPosition): Promise<void> {
    if (!context.serverContext && 'timeSlots' in entity.toJSON()) entity.timeSlots = undefined;
    await entity.validator.validateKeys(entity, ['meetingLink']);
    if (entity.meetingLink) entity.meetingLink = normalizeHttpUrl(entity.meetingLink);
    return super.beforeCreate(entity);
  }

  static override async transformBeforeUpdate(
    oldEntity: PartialEntityData<JobPosition>,
    update: JobPosition,
  ): Promise<void> {
    if ('meetingLink' in update.toJSON()) {
      await update.validator.validateKeys(update, ['meetingLink']);
      if (update.meetingLink) update.meetingLink = normalizeHttpUrl(update.meetingLink);
    }

    // Invalidate cached matching scores on update -> They will be recalculated on next load
    const matchingScoreCacheCol = mongo().collection<MatchingScoreCache>(MatchingScoreCache._collectionName);
    await matchingScoreCacheCol.deleteMany({ jobPositionID: oldEntity._id });

    // A client may submit the whole job form, but only addInterviewSlots may edit its slots.
    if (!context.serverContext && 'timeSlots' in update.toJSON()) update.timeSlots = undefined;

    if (context.serverContext && Array.isArray(update.timeSlots)) {
      const jobApplicationCol = mongo().collection(JobApplication._collectionName);
      const bookedApplications = await jobApplicationCol
        .find(
          {
            jobPositionId: oldEntity._id,
            status: JobApplicationStatus.BOOKED,
            interviewStart: { $exists: true, $ne: null },
            interviewEnd: { $exists: true, $ne: null },
          },
          { projection: { interviewStart: 1, interviewEnd: 1 } },
        )
        .toArray();

      update.timeSlots = update.timeSlots.filter(
        slot =>
          !slot.start ||
          !slot.end ||
          !bookedApplications.some(application =>
            overlapsInterval(
              { start: slot.start!, end: slot.end! },
              { start: application.interviewStart, end: application.interviewEnd },
            ),
          ),
      );
    }

    return super.transformBeforeUpdate(oldEntity, update);
  }
}
